// src/components/modalForm/UseModalFormFocus.js
import { useEffect, useRef } from 'react';

let backgroundLocks = 0;
let previousInert = false;

const getFocusable = (modal) => [...modal.querySelectorAll(
  'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'
)].filter((element) => element.tabIndex >= 0 && !element.closest('[inert], [hidden]') && element.getClientRects().length > 0);

const useModalFormFocus = ({ show, enabled, isTopModal, modalRef, onHide }) => {
  const openerRef = useRef(null);
  const visibleRef = useRef(false);
  const onHideRef = useRef(onHide);
  onHideRef.current = onHide;
  if (show && !visibleRef.current) openerRef.current = document.activeElement;
  visibleRef.current = show;

  useEffect(() => {
    if (!show || !enabled) return undefined;
    const root = document.getElementById('root');
    if (root) {
      if (backgroundLocks === 0) previousInert = root.hasAttribute('inert');
      backgroundLocks += 1;
      root.setAttribute('inert', '');
    }
    return () => {
      if (root && --backgroundLocks === 0 && !previousInert) root.removeAttribute('inert');
      const opener = openerRef.current;
      // Aguarda a atualização da pilha para devolver o foco ao cadastro abaixo.
      queueMicrotask(() => {
        if (opener?.isConnected && !opener.closest('[inert]')) opener.focus();
      });
    };
  }, [show, enabled]);

  useEffect(() => {
    if (!show || !enabled || !isTopModal) return undefined;
    const modal = modalRef.current;
    if (!modal) return undefined;
    // Preserva o autoFocus dos campos e o foco restaurado por uma busca.
    if (!modal.contains(document.activeElement)) {
      const field = modal.querySelector('[data-modal-autofocus="true"]:not(:disabled)')
        || modal.querySelector('.form-control:not(:disabled), .form-select:not(:disabled)');
      (field || getFocusable(modal)[0] || modal).focus();
    }
    const handleFocus = (event) => {
      const targetModal = event.target.closest('[data-modal-stack-id]');
      if (targetModal && Number(targetModal.style.zIndex) > Number(modal.style.zIndex)) return;
      if (!modal.contains(event.target)) (getFocusable(modal)[0] || modal).focus();
    };
    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, [show, enabled, isTopModal, modalRef]);

  return (event) => {
    if (enabled && isTopModal && !event.defaultPrevented && modalRef.current?.contains(event.target)) {
      if (event.key === 'Escape' && onHideRef.current) {
        event.preventDefault();
        onHideRef.current();
      } else if (event.key === 'Tab') {
        const focusable = getFocusable(modalRef.current);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) {
          event.preventDefault();
          modalRef.current.focus();
        } else if (event.shiftKey && (document.activeElement === first || document.activeElement === modalRef.current)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    event.stopPropagation();
  };
};

export default useModalFormFocus;
